import React, { useState, useContext, useEffect } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ClientNameContext } from "../contexts/clientNameContext";
import { SvgSuccessAlert } from "../components/Svgs";
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../Config';

const getCsrfToken = () => {
  const cookies = document.cookie.split(';').map(cookie => cookie.trim());
  //console.log("Cookies:", cookies);  // Verifique todos os cookies para ver se o CSRF token está presente
  const csrfCookie = cookies.find(cookie => cookie.startsWith('csrftoken='));
  //console.log("CSRF Cookie:", csrfCookie);  // Verifique o valor do cookie
  return csrfCookie ? csrfCookie.split('=')[1] : null;
};


async function fetchCsrfToken() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/csrf_token/`, {
      method: 'GET',
      credentials: 'include', // Garante que os cookies sejam enviados e recebidos
    });

    if (!response.ok) throw new Error("Erro ao obter CSRF Token");

    // Como o Django define o CSRF nos cookies, pegamos ele do document.cookie
    const csrfToken = getCsrfToken(); 
    //console.log("CSRF Token recuperado dos cookies:", csrfToken);
    return csrfToken;
  } catch (error) {
    //console.error("Erro na solicitação do CSRF Token:", error);
    return null;
  }
}


function LoginScreen() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const { setClientName } = useContext(ClientNameContext);

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchCsrfToken(); // Tente pegar o token CSRF
  }, []);

  const handleLogin = async (email, password) => {
    try {
      const csrfToken = await fetchCsrfToken(); // Chama a função que recupera o CSRF Token
      //console.log("CSRF Token: ", csrfToken);
  
      if (!csrfToken) {
        throw new Error("CSRF Token não encontrado nos cookies!");
      }
  
      const response = await fetch(`${API_BASE_URL}/api/login_verify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,  // Passa o token CSRF corretamente nos headers
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',  // Certifique-se de que os cookies estão sendo enviados
      });
  
      /*
      console.log('Request Headers:', {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken
      });
      */
  
      //console.log('Response Status:', response.status);
      //console.log('Response Status Text:', response.statusText);
      //console.log('Response Headers:', response.headers);
  
      if (!response.ok) {
        const errorData = await response.json();  // Obter a resposta JSON para mais detalhes
        console.log('Error Data:', errorData);
        throw new Error('Seus dados não estão corretos.');
      }
  
      const data = await response.json();
  
      // Armazene os dados do cliente apenas se a resposta do login for bem-sucedida
      localStorage.setItem("clientId", data.clientId);
      // client-id do paypal no localStorage para testes
      //localStorage.setItem("clientId", "AbwUonvXl5ia1PNyMYcS-lsHHiGcQvxV7AsydryS9HKxVZ2IxXSQJshJAuLV5HoDTwMQ6-A4GkQ1AULM");           
      localStorage.setItem("clientName", data.clientName);
      localStorage.setItem("clientSurname", data.clientSurname);
      localStorage.setItem("clientEmail", data.clientEmail);
      localStorage.setItem("clientAddress", data.clientAddress);
      localStorage.setItem("clientNumber", data.clientNumber);
      localStorage.setItem("clientAdditionalAddress", data.clientAdditionalAddress || "");
      localStorage.setItem("clientPostalCode", data.clientPostalCode);
      localStorage.setItem("clientCity", data.clientCity);
      localStorage.setItem("clientCountry", data.clientCountry);

      setClientName(data.clientName);
      setShowSuccess(true);
      navigate("/cart");
    } catch (error) {
      setShowError(true);
      //console.error("Erro no login:", error);
      if (error.message.includes('NetworkError')) {
        alert("Erro de rede ao tentar acessar o servidor. Por favor, verifique sua conexão.");
      } else if (error.message.includes('CSRF Token não encontrado')) {
        alert("CSRF Token não encontrado nos cookies. Por favor, tente novamente.");
      }
    }
  };
  

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin(email, password);
  };


  useEffect(() => {
    /*
    console.log("clientId: ", localStorage.getItem('clientId'));
    console.log("clientName: ", localStorage.getItem('clientName'));
    console.log("clientSurname: ", localStorage.getItem('clientSurname'));
    console.log("clientEmail: ", localStorage.getItem('clientEmail'));
    console.log("clientAddress: ", localStorage.getItem('clientAddress'));
    console.log("clientNumber: ", localStorage.getItem('clientNumber'));
    console.log("clientAdditionalAddress: ", localStorage.getItem('clientAdditionalAddress'));
    console.log("clientPostalCode: ", localStorage.getItem('clientPostalCode'));
    console.log("clientCity: ", localStorage.getItem('clientCity'));
    console.log("clientCountry: ", localStorage.getItem('clientCountry'));
    */
  }, []);

  return (
    <Container className="pt-4">
      <Row>
        <Col lg={5} md={6} className="align-items-center m-auto">
          {showError && (
            <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
              {t('invalid_credentials')}
            </Alert>
          )}

          {showSuccess && (
            <Alert
              variant="success"
              onClose={() => setShowSuccess(false)}
              dismissible
            >
              <Alert.Heading>
                <SvgSuccessAlert />
              </Alert.Heading>
              {t('welcome_message', { clientName: localStorage.getItem("clientName") })}
            </Alert>
          )}
        </Col>
      </Row>

      <Row className="py-lg-3">
        <Col lg={5} md={6} className="align-items-center m-auto">
          <h2 className="h4 mb-4">{t('login')}</h2>
          <Form onSubmit={handleSubmit} className="p-4 mt-4 mt-md-0 m-auto forms">
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label>E-Mail</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBasicPassword">
              <Form.Label>{t('password')}</Form.Label>
              <Form.Control
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="showPasswordCheckbox"
                label={<label htmlFor="showPasswordCheckbox" style={{ cursor: "pointer" }}>{t('show_password')}</label>}
                onChange={() => setShowPassword(!showPassword)}
              />
            </Form.Group>

            <Button variant="primary" type="submit" className="mb-3 me-2" title={t('confirm')}>
              {t('confirm')}
            </Button>
          </Form>
        </Col>
      </Row>

      <Row className="my-3 text-center">
        <Col>
          {t('not_registered')} <Link to="/register" title="Register">{t('click_here')}</Link>
        </Col>
      </Row>

      <Row className="my-3 text-center">
        <Col>
          {t('forgot_password')} <Link to="/password_forgot" title="Forgot">{t('click_here')}</Link>
        </Col>
      </Row>
    </Container>
  );
}

export default LoginScreen;
